import { Controller } from '@hotwired/stimulus'

interface TurboFrameElement extends HTMLElement {
  src: string | null
  reload(): void
}

// Reloads a READY results frame of a RUNNING election on a slow cadence so
// background re-warms actually reach the open page. The controller lives
// inside the frame content, so every reload re-attaches it; once the election
// completes the fragment renders without it and the polling stops naturally.
// Jittered so a 100-question page doesn't fire every frame in sync.
export default class extends Controller<HTMLElement> {
  static values = {
    interval: { type: Number, default: 60000 },
  }

  declare readonly intervalValue: number

  private timer?: ReturnType<typeof setTimeout>

  connect(): void {
    const frame = this.frame()
    if (!frame || !frame.src) return

    this.schedule(frame)
  }

  disconnect(): void {
    if (this.timer) clearTimeout(this.timer)
  }

  // Re-arms after every tick: a successful reload replaces the frame content
  // (disconnect clears the pending timer, the fresh controller re-connects),
  // while a FAILED reload leaves this instance alive — without the re-arm one
  // network blip would end the live polling for good.
  private schedule(frame: TurboFrameElement): void {
    const jittered = this.intervalValue * (0.9 + Math.random() * 0.2)
    this.timer = setTimeout(() => {
      if (!this.weightSwitchOff()) frame.reload()
      this.schedule(frame)
    }, jittered)
  }

  // Reloading re-renders the weighted default, hard-resetting an admin's
  // off-toggled Weighted Ballots switch — skip the tick but keep polling.
  private weightSwitchOff(): boolean {
    return !!this.element.querySelector('[data-weight-switch-checked="false"]')
  }

  private frame(): TurboFrameElement | null {
    return this.element.closest('turbo-frame') as TurboFrameElement | null
  }
}
